import { defineStore } from 'pinia'

import AuthScope from '~/enums/auth-scope'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { Page } from '~/types/json-api'
import type { SupplierFaqData } from '~/types/supplier-faqs'

export const messages = {
  failedToCreateSupplierFaq: 'Не удалось создать FAQ.',
  failedToDeleteSupplierFaq: 'Не удалось удалить FAQ.',
  failedToGetSupplierFaqs: 'Не удалось получить FAQs.',
  failedToGetSupplierFaq: 'Не удалось получить FAQ.',
  failedToUpdateSupplierFaq: 'Не удалось сохранить FAQ.',
  couldNotFindSupplierFaqs: 'FAQs не найдены.',
  emptySupplierFaqs: 'Нет часто задаваемых вопросов.',
  supplierFaqIsCreated: 'FAQ создан.',
  supplierFaqIsSaved: 'FAQ сохранен.',
  supplierFaqIsDeleted: 'FAQ удален.'
}

type State = {
  filter: {
    query: string
  },
  form: any
  hasUnsavedChanges: boolean
  isSupplierFaqsDataLoading: boolean
  isSupplierFaqDataLoading: boolean
  isDataLoadingError: boolean
  isLoading: boolean
  page: Page
  supplierFaqsData: SupplierFaqData[]
  supplierFaqData: SupplierFaqData|null
  totalPages: number|null
  totalSupplierFaqs: number|null
}

const initialState = (): State => ({
  filter: {
    query: ''
  },
  form: {
    answer: null,
    question: null
  },
  hasUnsavedChanges: false,
  isSupplierFaqsDataLoading: false,
  isSupplierFaqDataLoading: false,
  isDataLoadingError: false,
  isLoading: false,
  page: {
    number: 1,
    size: 20
  },
  supplierFaqsData: [],
  supplierFaqData: null,
  totalPages: null,
  totalSupplierFaqs: null
})

export const useSupplierFaqsStore = defineStore('supplierFaqs', {
  state: () => initialState(),
  getters: {
    getSupplierFaq: (state: State) => state.supplierFaqData,
    getSupplierFaqs: (state: State) => state.supplierFaqsData
  },
  actions: {
    clearFilter (): void {
      this.filter = {
        query: ''
      }
    },
    clearForm (): void {
      this.form.answer = null
      this.form.question = null
    },
    clearSupplierFaq (): void {
      this.supplierFaqData = null
    },
    clearSupplierFaqs (): void {
      this.supplierFaqsData = []
    },
    dispose (): void {
      this.clearFilter()
      this.clearSupplierFaqs()
    },
    supplierFaqToForm (): void {
      if (this.supplierFaqData) {
        this.clearForm()

        this.form.answer = this.supplierFaqData.attributes.answer
        this.form.question = this.supplierFaqData.attributes.question
      }
    },
    async createSupplierFaq (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        const response = await $apiHelper.supplierFaqs.createSupplierFaq({
          answer: this.form.answer,
          question: this.form.question
        }) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.supplierFaqData = data

        this.supplierFaqToForm()

        this.supplierFaqsData.push(data)

        $toast.success(messages.supplierFaqIsCreated)
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToCreateSupplierFaq)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    },
    async deleteSupplierFaq (supplierFaqId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        await $apiHelper.supplierFaqs.deleteSupplierFaq(supplierFaqId) as any

        const supplierFaqIndex = this.supplierFaqsData.findIndex((x: SupplierFaqData) => x.id === supplierFaqId)
        this.supplierFaqsData.splice(supplierFaqIndex, 1)

        $toast.success(messages.supplierFaqIsDeleted)
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToDeleteSupplierFaq)
      }

      this.isLoading = false
    },
    async fetchSupplierFaq (supplierFaqId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isSupplierFaqDataLoading = true
      this.isDataLoadingError = false

      try {
        const response = await $apiHelper.supplierFaqs.getSupplierFaq(supplierFaqId) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.supplierFaqData = data

        this.supplierFaqToForm()

        this.isSupplierFaqDataLoading = false
      } catch (error) {
        this.isDataLoadingError = true

        console.error(error)
        $toast.error(messages.failedToGetSupplierFaq)
      }
    },
    async fetchSupplierFaqs (authScope = AuthScope.Admin): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isSupplierFaqsDataLoading = true
      this.totalPages = null

      try {
        const response = await $apiHelper.supplierFaqs.getSupplierFaqs({
          filter: this.filter,
          page: this.page
        }, authScope) as any
        const { data, meta } = JsonApiHelper.denormalizeResponse(response) as any

        this.totalSupplierFaqs = meta.totalSupplierFaqs
        this.totalPages = helpers.getTotalPages(meta.totalSupplierFaqs, this.page.size)

        this.supplierFaqsData = data

        this.isSupplierFaqsDataLoading = false
      } catch (error) {
        console.error(error)
        $toast.error(messages.failedToGetSupplierFaqs)
      }
    },
    async updateSupplierFaq (supplierFaqId: string): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isLoading = true

      try {
        const response = await $apiHelper.supplierFaqs.updateSupplierFaq(supplierFaqId, {
          answer: this.form.answer,
          question: this.form.question
        }) as any
        const { data } = JsonApiHelper.denormalizeResponse(response) as any

        this.supplierFaqData = data

        const faqIndex = this.supplierFaqsData.findIndex((faq: SupplierFaqData) => faq.attributes.id === data.attributes.id)

        this.supplierFaqsData[faqIndex] = data

        $toast.success(messages.supplierFaqIsSaved)

        this.supplierFaqToForm()
      } catch (error) {
        console.error(error)

        $toast.error(messages.failedToUpdateSupplierFaq)
      }

      this.hasUnsavedChanges = false
      this.isLoading = false
    }
  }
})
